import { computed, onMounted, ref } from 'vue';

import { apiFetch } from '@ja/portal-query';

interface VapidKeyResponse {
    configured: boolean;
    public_key: string | null;
}

/** Strip whitespace so line-wrapped keys from .env/API still decode correctly. */
function normalizeVapidPublicKey(key: string): string {
    return key.trim().replace(/\s+/g, '');
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const normalized = normalizeVapidPublicKey(base64String);
    const padding = '='.repeat((4 - (normalized.length % 4)) % 4);
    const base64 = (normalized + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i += 1) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

/**
 * Subscribe the current browser to Web Push (PWA) after permission + SW ready.
 */
export type VapidServerStatus = 'unknown' | 'ready' | 'missing' | 'error';

/** Must match registerPwaUpdates() in the Employee portal entry. */
const PORTAL_SW_SCRIPT_URL = '/sw.js';
const PORTAL_SW_SCOPE = '/';

/** Avoid hammering GET vapid-public-key when the panel opens + trySync runs. */
const VAPID_META_CACHE_MS = 30_000;

function isDomExceptionPushSubscribeFailure(e: unknown): boolean {
    if (!(e instanceof DOMException)) {
        return false;
    }
    const m = e.message.toLowerCase();
    return (
        /push service/i.test(e.message) ||
        (m.includes('registration') && m.includes('push')) ||
        (m.includes('inscription') && m.includes('push'))
    );
}

/**
 * Push subscribe must use a real registration. `navigator.serviceWorker.ready` never
 * resolves in Chrome when no SW is registered, so we would hang after VAPID fetch and
 * never POST /push/subscribe. registerPwaUpdates() also waits for window.load.
 */
async function ensureServiceWorkerForPush(): Promise<ServiceWorkerRegistration> {
    try {
        let registration =
            (await navigator.serviceWorker.getRegistration(PORTAL_SW_SCOPE)) ??
            (await navigator.serviceWorker.getRegistration());
        if (registration == null) {
            const existing = await navigator.serviceWorker.getRegistrations();
            registration = existing[0] ?? null;
        }
        if (registration == null) {
            registration = await navigator.serviceWorker.register(PORTAL_SW_SCRIPT_URL, {
                scope: PORTAL_SW_SCOPE,
            });
        }
        await registration.ready;
        return registration;
    } catch {
        throw new Error('error_sw_registration');
    }
}

export function usePortalWebPush() {
    const supported = ref(false);
    const vapidConfigured = ref(false);
    /** Distinct from missing keys: API failed (auth, network, 5xx). */
    const vapidServerStatus = ref<VapidServerStatus>('unknown');
    const permission = ref<NotificationPermission>('default');
    const hasSubscription = ref(false);
    const busy = ref(false);
    const testPushBusy = ref(false);
    const lastError = ref<string | null>(null);
    const vapidMetaCache = ref<{ key: string; at: number } | null>(null);

    onMounted(() => {
        supported.value =
            typeof window !== 'undefined' &&
            'serviceWorker' in navigator &&
            'PushManager' in window &&
            'Notification' in window;
        if (supported.value) {
            permission.value = Notification.permission;
        }
        void refreshSubscriptionState();
    });

    /**
     * Detect push subscription on any SW registration for this origin.
     * `getRegistration()` alone often misses the same registration used by
     * `navigator.serviceWorker.ready` + subscribe() (e.g. scope / not yet controlling).
     */
    async function refreshSubscriptionState(): Promise<void> {
        if (!supported.value) {
            return;
        }
        try {
            const registrations = await navigator.serviceWorker.getRegistrations();
            let found = false;
            for (const reg of registrations) {
                const sub = await reg.pushManager.getSubscription();
                if (sub) {
                    found = true;
                    break;
                }
            }
            hasSubscription.value = found;
        } catch {
            hasSubscription.value = false;
        }
    }

    /** Call when opening UI (e.g. notification panel) so labels reflect server config. */
    async function refreshAvailability(): Promise<void> {
        if (!supported.value) {
            return;
        }
        try {
            await fetchVapidMeta();
        } catch {
            vapidConfigured.value = false;
            vapidServerStatus.value = 'error';
        }
        await refreshSubscriptionState();
        if (typeof Notification !== 'undefined') {
            permission.value = Notification.permission;
        }
    }

    async function fetchVapidMeta(): Promise<{ key: string } | null> {
        const now = Date.now();
        const cached = vapidMetaCache.value;
        if (cached !== null && now - cached.at < VAPID_META_CACHE_MS) {
            vapidConfigured.value = true;
            vapidServerStatus.value = 'ready';
            return { key: cached.key };
        }

        try {
            const res = await apiFetch<VapidKeyResponse>('/api/notifications/push/vapid-public-key');
            vapidConfigured.value = res.configured;
            vapidServerStatus.value = res.configured && res.public_key ? 'ready' : 'missing';
            if (!res.configured || !res.public_key) {
                vapidMetaCache.value = null;
                return null;
            }
            vapidMetaCache.value = { key: res.public_key, at: now };
            return { key: res.public_key };
        } catch (e) {
            vapidMetaCache.value = null;
            throw e;
        }
    }

    async function syncSubscriptionWithServer(): Promise<boolean> {
        lastError.value = null;
        busy.value = true;
        try {
            const vapid = await fetchVapidMeta();
            if (!vapid) {
                lastError.value = 'error_not_configured';
                return false;
            }

            const registration = await ensureServiceWorkerForPush();
            const applicationServerKey = urlBase64ToUint8Array(vapid.key);
            if (applicationServerKey.length !== 65 && applicationServerKey.length !== 33) {
                lastError.value = 'error_invalid_vapid_key';
                return false;
            }

            const sub = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey,
            });

            const json = sub.toJSON();
            if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
                lastError.value = 'error_invalid_subscription';
                return false;
            }

            await apiFetch<{ message: string }>('/api/notifications/push/subscribe', {
                method: 'POST',
                body: JSON.stringify({
                    endpoint: json.endpoint,
                    keys: {
                        p256dh: json.keys.p256dh,
                        auth: json.keys.auth,
                    },
                }),
            });

            hasSubscription.value = true;
            await refreshSubscriptionState();
            return true;
        } catch (e) {
            if (e instanceof Error && e.message === 'error_sw_registration') {
                lastError.value = 'error_sw_registration';
            } else if (isDomExceptionPushSubscribeFailure(e)) {
                lastError.value = 'error_push_service';
            } else {
                lastError.value = e instanceof Error ? e.message : 'unknown_error';
            }
            return false;
        } finally {
            busy.value = false;
        }
    }

    async function enablePush(): Promise<boolean> {
        if (!supported.value) {
            return false;
        }
        const result = await Notification.requestPermission();
        permission.value = result;
        if (result !== 'granted') {
            return false;
        }
        return syncSubscriptionWithServer();
    }

    async function sendTestPush(): Promise<boolean> {
        lastError.value = null;
        testPushBusy.value = true;
        try {
            await apiFetch<{ message: string }>('/api/notifications/push/test', {
                method: 'POST',
            });
            return true;
        } catch (e) {
            lastError.value = e instanceof Error ? e.message : 'unknown_error';
            return false;
        } finally {
            testPushBusy.value = false;
        }
    }

    async function disablePush(): Promise<void> {
        lastError.value = null;
        busy.value = true;
        try {
            const registrations = await navigator.serviceWorker.getRegistrations();
            for (const reg of registrations) {
                const sub = await reg.pushManager.getSubscription();
                if (sub) {
                    const endpoint = sub.endpoint;
                    await sub.unsubscribe();
                    await apiFetch<{ message: string }>('/api/notifications/push/subscribe', {
                        method: 'DELETE',
                        body: JSON.stringify({ endpoint }),
                    });
                    break;
                }
            }
            await refreshSubscriptionState();
        } catch (e) {
            lastError.value = e instanceof Error ? e.message : 'unknown_error';
        } finally {
            busy.value = false;
        }
    }

    /**
     * If permission is already granted, sync browser subscription to the server when missing.
     * Skips when a push subscription already exists (avoids duplicate GET vapid + subscribe attempts).
     */
    async function trySyncExistingSubscription(): Promise<void> {
        if (!supported.value || Notification.permission !== 'granted') {
            return;
        }
        await refreshSubscriptionState();
        if (hasSubscription.value) {
            return;
        }
        await syncSubscriptionWithServer();
    }

    const canToggle = computed(
        () => supported.value && vapidConfigured.value && permission.value !== 'denied',
    );

    return {
        supported,
        vapidConfigured,
        vapidServerStatus,
        permission,
        hasSubscription,
        busy,
        testPushBusy,
        lastError,
        canToggle,
        enablePush,
        disablePush,
        sendTestPush,
        trySyncExistingSubscription,
        refreshSubscriptionState,
        refreshAvailability,
    };
}
